import { useEffect, useState } from 'react';
import {
  docHasText, docsEqual, type AdvisorDoc, type AdvisorVersionMeta, type ClientAdvisorSummaryAdmin,
} from '@agio/core/domain';
import { Card } from '../../Card.js';
import { ConfirmDialog } from '../../ConfirmDialog.js';
import { ErrorBanner, Spinner } from '../../FormBits.js';
import { AdvisorEditor } from '../../advisor/AdvisorEditor.js';
import { AdvisorRichText } from '../../advisor/AdvisorRichText.js';
import { advisor, fmtWhen } from './advisorApi.js';
import { resetPlan } from './resetPlan.js';
import { cn } from '../../../utils.js';

type Pending = 'copy' | 'publish' | 'reset' | 'undo' | null;

/**
 * One client's summary: copy a version in, edit the draft, publish it — and
 * the two ways back (reset the draft, undo the last publish). The draft is
 * never shown to the client; the preview at the bottom is exactly what is.
 */
export function ClientSummaryPanel({
  clientId,
  clientName,
  onUnsavedChange,
  onChanged,
}: {
  clientId: string;
  clientName: string;
  /** Told the draft's unsaved typing whenever it changes (null once all is saved), so a client switch can ask first. */
  onUnsavedChange: (doc: AdvisorDoc | null) => void;
  /** Told after every change, so the status table can re-read. */
  onChanged: () => void;
}) {
  const [summary, setSummary] = useState<ClientAdvisorSummaryAdmin | null>(null);
  const [versions, setVersions] = useState<AdvisorVersionMeta[]>([]);
  const [picked, setPicked] = useState<number | null>(null);
  const [local, setLocal] = useState<AdvisorDoc | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([advisor.summary(clientId), advisor.versions()])
      .then(([s, v]) => {
        if (!live) return;
        setSummary(s);
        setLocal(s.draft);
        setVersions(v);
        setPicked((p) => p ?? v[0]?.version ?? null);
      })
      .catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [clientId]);

  const unsavedDoc = summary && local && !docsEqual(local, summary.draft) ? local : null;
  useEffect(() => {
    onUnsavedChange(unsavedDoc);
  }, [unsavedDoc, onUnsavedChange]);

  if (!summary) return <Card><ErrorBanner error={error} title="שגיאה" />{!error && <Spinner />}</Card>;

  const unsaved = !docsEqual(local, summary.draft);
  const editsToPublish = summary.hasUnpublishedEdits || unsaved;
  const draftHasText = docHasText(local);

  /** Run one action; the server answers with the fresh state, which replaces ours. */
  const run = async (action: () => Promise<ClientAdvisorSummaryAdmin>, done: string, keepLocal = false) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const s = await action();
      setSummary(s);
      if (!keepLocal) setLocal(s.draft);
      setNote(done);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const copy = () => picked !== null && run(() => advisor.copy(clientId, picked), `גרסה ${picked} הועתקה לטיוטה`);
  const saveDraft = () => local && run(() => advisor.saveDraft(clientId, local), 'הטיוטה נשמרה', true);
  const publish = () => run(async () => {
    // Unsaved typing is saved first, so what is published is what is on screen.
    if (unsaved && local) await advisor.saveDraft(clientId, local);
    return advisor.publish(clientId);
  }, 'פורסם ללקוח');
  const reset = () => {
    const plan = resetPlan(summary, unsaved);
    if (plan === 'server') {
      void run(() => advisor.reset(clientId), 'הטיוטה אופסה למה שמפורסם');
      return;
    }
    // Only unsaved typing to throw away: the editor goes back to what is stored
    // (empty when nothing ever was) — the server has nothing to reset.
    setLocal(summary.draft);
    setError(null);
    setNote(plan === 'local' ? 'השינויים שלא נשמרו נמחקו' : null);
    setPending(null);
  };
  const undo = () => run(() => advisor.undoPublish(clientId), 'הפרסום האחרון בוטל');

  const btn = 'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="grid grid-cols-1 gap-5">
      <Card title={`טיוטה ללקוח · ${clientName}`}>
        <div className="grid grid-cols-1 gap-4">
          {summary.newerVersionAvailable && summary.latestVersion !== null && (
            <p role="note" className="rounded-lg border border-ring/40 bg-ring/10 px-4 py-2.5 text-sm">
              {summary.copiedFromVersion === null
                ? `ללקוח הזה עדיין לא הועתקה גרסה. הגרסה הראשית האחרונה היא ${summary.latestVersion}.`
                : `יש גרסה ראשית חדשה יותר (${summary.latestVersion}) מזו שהועתקה ללקוח (${summary.copiedFromVersion}).`}
            </p>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">גרסה ראשית</span>
              <select
                value={picked ?? ''}
                onChange={(e) => setPicked(Number(e.target.value))}
                disabled={versions.length === 0}
                className="rounded-md border border-input bg-popover px-3 py-2"
              >
                {versions.length === 0 && <option value="">אין גרסאות עדיין</option>}
                {versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    גרסה {v.version} · {fmtWhen(v.createdAt)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={busy || picked === null}
              onClick={() => (summary.draft !== null || unsaved ? setPending('copy') : copy())}
              className={cn(btn, 'border border-border hover:bg-muted')}
            >
              העתקה מהגרסה
            </button>
            {summary.copiedFromVersion !== null && (
              <span className="text-sm text-muted-foreground">הטיוטה הועתקה מגרסה {summary.copiedFromVersion}</span>
            )}
          </div>

          <AdvisorEditor value={local} onChange={(d) => { setLocal(d); setNote(null); }} label="טיוטת הסקירה ללקוח" />
          <p className="text-xs text-muted-foreground">
            {summary.draftUpdatedAt ? `הטיוטה נשמרה ${fmtWhen(summary.draftUpdatedAt)} · ${summary.draftUpdatedBy}` : 'אין טיוטה שמורה'}
            {unsaved && ' · יש שינויים שלא נשמרו'}
          </p>

          <ErrorBanner error={error} title="הפעולה נכשלה" />

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy || !unsaved || !local}
              onClick={saveDraft}
              className={cn(btn, 'border border-border hover:bg-muted')}
            >
              שמירת טיוטה
            </button>
            <button
              type="button"
              disabled={busy || !editsToPublish || !draftHasText}
              onClick={() => setPending('publish')}
              className={cn(btn, 'bg-primary text-primary-foreground hover:opacity-90')}
            >
              {busy && pending === 'publish' && <Spinner />}
              פרסום
            </button>
            <button
              type="button"
              disabled={busy || !editsToPublish}
              onClick={() => setPending('reset')}
              className={cn(btn, 'border border-border hover:bg-muted')}
            >
              איפוס
            </button>
            <button
              type="button"
              disabled={busy || !summary.canUndoPublish}
              onClick={() => setPending('undo')}
              className={cn(btn, 'border border-destructive/50 text-destructive hover:bg-destructive/10')}
            >
              ביטול פרסום אחרון
            </button>
            {note && <span role="status" className="text-sm font-semibold text-success">{note}</span>}
          </div>
        </div>
      </Card>

      <Card title={`מה ${clientName} רואה עכשיו`}>
        {summary.published ? (
          <div className="grid grid-cols-1 gap-3">
            <p className="text-xs text-muted-foreground">
              פורסם {fmtWhen(summary.publishedAt)} · {summary.publishedBy}
              {summary.publishedFromVersion !== null && ` · מגרסה ${summary.publishedFromVersion}`}
            </p>
            <div className="rounded-lg border border-border p-4">
              <AdvisorRichText doc={summary.published} />
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">לא פורסם — הלקוח לא רואה סקירת יועץ.</p>
        )}
      </Card>

      <ConfirmDialog
        open={pending === 'copy'}
        title="להחליף את הטיוטה?"
        confirmLabel="העתקה והחלפה"
        tone="danger"
        onConfirm={copy}
        onCancel={() => setPending(null)}
      >
        ללקוח כבר יש טיוטה{unsaved ? ' (וגם שינויים שלא נשמרו)' : ''}. העתקת גרסה {picked} תחליף אותה. מה שהלקוח רואה לא ישתנה עד לפרסום.
      </ConfirmDialog>
      <ConfirmDialog
        open={pending === 'publish'}
        title="לפרסם ללקוח?"
        confirmLabel="פרסום"
        onConfirm={publish}
        onCancel={() => setPending(null)}
      >
        הטיוטה תוצג ל{clientName} מיד, בפורטל ובדוח המודפס, עד לפרסום הבא. אפשר לבטל את הפרסום הזה פעם אחת.
      </ConfirmDialog>
      <ConfirmDialog
        open={pending === 'reset'}
        title="לאפס את הטיוטה?"
        confirmLabel="איפוס"
        tone="danger"
        onConfirm={reset}
        onCancel={() => setPending(null)}
      >
        כל השינויים שלא פורסמו יימחקו, והטיוטה תחזור להיות בדיוק מה שהלקוח רואה עכשיו
        {summary.published ? '' : ' (כלומר ריקה — עדיין לא פורסם דבר)'}.
      </ConfirmDialog>
      <ConfirmDialog
        open={pending === 'undo'}
        title="לבטל את הפרסום האחרון?"
        confirmLabel="ביטול הפרסום"
        tone="danger"
        cancelLabel="חזרה"
        onConfirm={undo}
        onCancel={() => setPending(null)}
      >
        הלקוח יראה שוב את הטקסט שפורסם לפני הפרסום האחרון. אם זה היה הפרסום הראשון — הסקירה תוסר מהלקוח.
        הטיוטה לא משתנה. אפשר לבטל רק פרסום אחד אחורה.
      </ConfirmDialog>
    </div>
  );
}
